<template>
  <div class="ts-space-y-2">
    <!-- Bank Logo -->
    <div
      class="hover:ts-shadow-md ts-border ts-cursor-pointer ts-border-gray-300 ts-mb-4 ts-rounded-md ts-overflow-hidden ts-mx-auto ts-w-1/2"
    >
      <div class="ts-pt-1/2 ts-relative">
        <label for="logo-image" class="ts-w-full ts-h-full ts-cursor-pointer ts-absolute ts-top-0">
          <img id="logo" class="ts-w-full ts-h-full ts-object-contain" :src="logo_file_src" alt />
        </label>
      </div>
      <input type="file" id="logo-image" ref="logoimage" style="display:none" @change="onFileChange" accept="image/*" />
    </div>

    <!-- Bank Name + Is Default -->
    <div class="row">
      <div class="col-md-9">
        <label class="ts-text-xs required">{{ $t('bankProfile.bankName') }}</label>
        <input
          v-model="model.bank_name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.has('bank_name') }"
        />
        <div class="invalid-feedback ts-text-xs" v-if="errors.has('bank_name')">
          {{ errors.first('bank_name') }}
        </div>
      </div>
      <div class="col-md-3 d-flex align-items-end pb-1">
        <a-tooltip placement="top" title="Only one bank can be default per company. Setting this will unset the current default.">
          <a-checkbox :checked="model.is_default" @change="model.is_default = $event.target.checked">
            <span class="ts-text-sm ts-font-semibold">Default Bank</span>
          </a-checkbox>
        </a-tooltip>
      </div>
    </div>

    <!-- Account Numbers -->
    <div class="row">
      <div class="col-md-12">
        <label class="required ts-text-xs">{{ $t('bankProfile.accountNumber') }}</label>
        <div class="ts-border ts-border-blue-300 ts-rounded ts-p-2 ts-relative">
          <div class="overflow-auto" style="min-height: 300px; max-height: 450px">
            <table style="width:100%;border-collapse:collapse;">
              <thead>
                <tr class="ts-uppercase ts-bg-gray-200 ts-sticky ts-top-0">
                  <th class="ts-p-1 ts-border-white ts-border" style="width:12%">
                    <label class="ts-m-0">{{ $t('bankProfile.prefix') }}</label>
                  </th>
                  <th class="ts-p-1 ts-border-white ts-border" style="width:18%">
                    <label class="required ts-m-0">{{ $t('bankProfile.accountNumber') }}</label>
                  </th>
                  <th class="ts-p-1 ts-border-white ts-border" style="width:18%">
                    <label class="required ts-m-0">{{ $t('bankProfile.accountName') }}</label>
                  </th>
                  <th class="ts-p-1 ts-border-white ts-border ts-text-center" style="width:8%">
                    <label class="ts-m-0">Default</label>
                  </th>
                  <th class="ts-p-1 ts-border-white ts-border ts-text-center" style="width:38%">
                    <label class="ts-m-0">KHQR Image</label>
                  </th>
                  <th class="ts-p-1 ts-border-white ts-border ts-text-center" style="width:6%">
                    <a href="#" class="text-primary" @click.prevent="addAccount">
                      <i class="fas fa-plus"></i>
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, index) in model.account_number" :key="index">
                  <td class="ts-p-1 ts-border-white ts-border ts-align-top">
                    <input
                      v-model.trim="a.prefix"
                      type="text"
                      class="form-control"
                      :placeholder="$t('bankProfile.prefix')"
                      :class="{ 'is-invalid': errors.has('account_number.' + index + '.prefix') }"
                    />
                  </td>
                  <td class="ts-p-1 ts-border-white ts-border ts-align-top">
                    <input
                      v-model.trim="a.account_number"
                      type="text"
                      class="form-control"
                      :placeholder="$t('bankProfile.accountNumber')"
                      :class="{ 'is-invalid': errors.has('account_number.' + index + '.account_number') }"
                    />
                  </td>
                  <td class="ts-p-1 ts-border-white ts-border ts-align-top">
                    <input
                      v-model.trim="a.account_name"
                      type="text"
                      class="form-control"
                      :placeholder="$t('bankProfile.accountName')"
                      :class="{ 'is-invalid': errors.has('account_number.' + index + '.account_name') }"
                    />
                  </td>
                  <td class="ts-p-1 ts-border-white ts-border ts-text-center ts-align-top" style="padding-top:10px;">
                    <input
                      type="radio"
                      name="account_default"
                      :checked="a.is_default"
                      @change="setDefaultAccount(index)"
                      style="width:18px;height:18px;cursor:pointer;"
                    />
                  </td>
                  <td class="ts-p-1 ts-border-white ts-border ts-align-top">
                    <div class="d-flex align-items-start ts-gap-2">
                      <div
                        class="ts-border ts-rounded ts-cursor-pointer ts-flex-shrink-0"
                        style="width:80px;height:80px;overflow:hidden;"
                        @click="triggerKhqrInput(index)"
                      >
                        <img
                          :src="a._khqr_src || require('@/assets/upload.png')"
                          style="width:100%;height:100%;object-fit:contain;"
                        />
                      </div>
                      <input
                        :ref="'khqr_' + index"
                        type="file"
                        style="display:none"
                        accept="image/*"
                        @change="onKhqrChange($event, index)"
                      />
                      <div class="ts-text-xs ts-text-gray-500 ts-mt-1">
                        <span v-if="a.khqr_image && !a._khqr_file" class="text-success">
                          <i class="fas fa-check-circle"></i> Uploaded
                        </span>
                        <span v-else-if="a._khqr_file" class="text-primary">
                          <i class="fas fa-file-image"></i> {{ a._khqr_file.name }}
                        </span>
                        <span v-else class="text-muted">Click to upload</span>
                        <br />
                        <a
                          v-if="a.khqr_image || a._khqr_src"
                          href="#"
                          class="text-danger ts-text-xs"
                          @click.prevent="clearKhqr(index)"
                        >
                          <i class="fas fa-times"></i> Remove
                        </a>
                      </div>
                    </div>
                  </td>
                  <td class="ts-p-1 ts-border-white ts-border ts-text-center ts-align-top" style="padding-top:10px;">
                    <a href="#" class="text-danger" @click.prevent="remove(index)">
                      <i class="fa fa-trash"></i>
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Description -->
    <div class="row">
      <div class="col-md-12">
        <label class="ts-text-xs">{{ $t('bankProfile.description') }}</label>
        <textarea rows="3" class="form-control" v-model.trim="model.description"></textarea>
        <div class="invalid-feedback ts-text-xs" v-if="errors.has('description')">
          {{ errors.first('description') }}
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="row">
      <div class="col-md-12 ts-mt-3 text-right">
        <ts-button class="mr-2" @click.prevent="() => $emit('cancel')">
          {{ $t('cancel') }}
        </ts-button>
        <ts-button v-if="!isUpdate" color="primary" @click.prevent="onSave" :waiting="waiting">
          {{ $t('save') }}
        </ts-button>
        <ts-button v-if="isUpdate" color="primary" @click.prevent="onUpdate" :waiting="waiting">
          {{ $t('update') }}
        </ts-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'

export default {
  name: 'bankProfileForm',
  data () {
    return {
      errors: new Errors(),
      waiting: false,
      waiting_new: false,
      logo_file: null,
      logo_file_src: require('@/assets/upload.png'),
      model: {
        bank_id: 0,
        bank_name: null,
        is_default: false,
        description: null,
        bank_logo: null,
        account_number: [
          {
            prefix: null,
            account_number: null,
            account_name: null,
            is_default: false,
            khqr_image: null,
            _khqr_file: null,
            _khqr_src: null
          }
        ]
      }
    }
  },
  created () {
    this.setEditData()
  },
  computed: {
    ...mapState('corporate/bankProfile', ['edit_data']),
    isUpdate () {
      return !_.isEmpty(this.edit_data)
    }
  },
  methods: {
    onFileChange () {
      const file = this.$refs.logoimage.files[0]
      if (!file) return
      if (!/\.(jpe?g|png)$/i.test(file.name)) {
        this.$toasted.error('Invalid file type!')
        return
      }
      let reader = new FileReader()
      reader.addEventListener('load', () => {
        this.logo_file_src = reader.result
      })
      reader.readAsDataURL(file)
      this.logo_file = file
    },
    triggerKhqrInput (index) {
      const ref = this.$refs['khqr_' + index]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el) el.click()
    },
    onKhqrChange (event, index) {
      const file = event.target.files[0]
      if (!file) return
      if (!/\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
        this.$toasted.error('Invalid file type!')
        return
      }
      let reader = new FileReader()
      reader.addEventListener('load', () => {
        this.$set(this.model.account_number[index], '_khqr_src', reader.result)
      })
      reader.readAsDataURL(file)
      this.$set(this.model.account_number[index], '_khqr_file', file)
    },
    clearKhqr (index) {
      this.$set(this.model.account_number[index], 'khqr_image', null)
      this.$set(this.model.account_number[index], '_khqr_file', null)
      this.$set(this.model.account_number[index], '_khqr_src', null)
    },
    setDefaultAccount (index) {
      this.model.account_number.forEach((a, i) => {
        this.$set(this.model.account_number[i], 'is_default', i === index)
      })
    },
    addAccount () {
      this.model.account_number.push({
        prefix: null,
        account_number: null,
        account_name: null,
        is_default: false,
        khqr_image: null,
        _khqr_file: null,
        _khqr_src: null
      })
    },
    remove (index) {
      this.model.account_number.splice(index, 1)
    },
    async uploadLogo () {
      if (this.logo_file instanceof File) {
        let formData = new FormData()
        formData.append('file', this.logo_file)
        this.model.bank_logo = await this.$store.dispatch('corporate/bankProfile/upload', formData)
      }
    },
    async uploadKhqrImages () {
      for (let i = 0; i < this.model.account_number.length; i++) {
        const a = this.model.account_number[i]
        if (a._khqr_file instanceof File) {
          let formData = new FormData()
          formData.append('file', a._khqr_file)
          const url = await this.$store.dispatch('corporate/bankProfile/upload', formData)
          this.$set(this.model.account_number[i], 'khqr_image', url)
        }
      }
    },
    buildPayload () {
      return {
        ...this.model,
        account_number: this.model.account_number.map(a => ({
          prefix: a.prefix,
          account_number: a.account_number,
          account_name: a.account_name,
          is_default: a.is_default,
          khqr_image: a.khqr_image
        }))
      }
    },
    async onSave () {
      this.waiting = true
      try {
        await this.uploadLogo()
        await this.uploadKhqrImages()
        const response = await this.$store.dispatch('corporate/bankProfile/store', this.buildPayload())
        this.$toasted.success(response.message)
        this.$emit('fetchData')
        this.$emit('cancel')
      } catch (error) {
        this.$toasted.error(error.message)
        this.errors = new Errors(error.errors)
      } finally {
        this.waiting = false
      }
    },
    async onUpdate () {
      this.waiting = true
      try {
        await this.uploadLogo()
        await this.uploadKhqrImages()
        const response = await this.$store.dispatch('corporate/bankProfile/update', {
          id: this.model.bank_id,
          data: this.buildPayload()
        })
        this.$toasted.success(response.message)
        this.$emit('fetchData')
        this.$emit('cancel')
      } catch (error) {
        this.$toasted.error(error.message)
        this.errors = new Errors(error.errors)
      } finally {
        this.waiting = false
      }
    },
    setEditData () {
      if (!_.isEmpty(this.edit_data)) {
        this.model.bank_id = this.edit_data.bank_id
        this.model.bank_name = this.edit_data.bank_name
        this.model.is_default = !!this.edit_data.is_default
        this.model.description = this.edit_data.description
        this.model.bank_logo = this.edit_data.bank_logo
        if (this.edit_data.bank_logo) {
          this.logo_file_src = this.edit_data.bank_logo
        }
        this.model.account_number = (this.edit_data.accounts || []).map(el => ({
          prefix: el.prefix,
          account_number: el.account_number,
          account_name: el.account_name,
          is_default: !!el.is_default,
          khqr_image: el.khqr_image || null,
          _khqr_file: null,
          _khqr_src: el.khqr_image || null
        }))
      }
    }
  }
}
</script>
